import React, { useEffect, useState, useCallback } from 'react';
import { financeiroAPI } from '../../lib/api';
import { useModulosAtivos } from '../../hooks/useModulosAtivos';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Plus, Pencil, Trash2, Tag, CreditCard, Receipt } from 'lucide-react';
import { toast } from 'sonner';
import { Navigate } from 'react-router-dom';

const fmt = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);

export default function FinanceiroConfiguracoes() {
  const { financeiroAtivo, loading: loadingMod } = useModulosAtivos();
  const [tab, setTab] = useState('tipos');

  if (loadingMod) return <div className="p-10 text-center text-muted-foreground">Carregando módulo...</div>;
  if (!financeiroAtivo) return <Navigate to="/" replace />;

  const tabs = [
    { key: 'tipos', label: 'Tipos de Serviço', icon: Tag },
    { key: 'metodos', label: 'Formas de Pagamento', icon: CreditCard },
    { key: 'categorias', label: 'Tipos de Despesas', icon: Receipt },
  ];

  return (
    <div className="space-y-5" data-testid="financeiro-config">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Configurações do Financeiro</h1>
        <p className="text-sm text-muted-foreground">Tipos de serviço, formas de pagamento e tipos de despesas</p>
      </div>

      <div className="flex gap-2 border-b">
        {tabs.map(t => {
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`tab-${t.key}`}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${tab === t.key ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
            >
              <Icon className="w-4 h-4" /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === 'tipos' && <ServiceTypesPanel />}
      {tab === 'metodos' && <PaymentMethodsPanel />}
      {tab === 'categorias' && <CategoriesPanel />}
    </div>
  );
}

// ---------- Service Types ----------
function ServiceTypesPanel() {
  const [list, setList] = useState([]);
  const [edit, setEdit] = useState(null);
  const [show, setShow] = useState(false);
  const load = async () => setList((await financeiroAPI.listServiceTypes()).data || []);
  useEffect(() => { load(); }, []);

  const onDelete = async (id) => {
    if (!window.confirm('Excluir?')) return;
    await financeiroAPI.deleteServiceType(id);
    toast.success('Excluído');
    load();
  };

  return (
    <Card><CardContent className="p-4">
      <div className="flex justify-between mb-4">
        <h3 className="font-semibold">Tipos de Serviço</h3>
        <Button onClick={() => { setEdit(null); setShow(true); }} data-testid="btn-add-tipo"><Plus className="w-4 h-4 mr-1" /> Novo</Button>
      </div>
      {list.length === 0 ? <p className="text-sm text-muted-foreground text-center py-6">Nenhum tipo cadastrado</p> : (
        <table className="w-full text-sm">
          <thead className="bg-muted/30"><tr>
            <th className="text-left p-2">Nome</th><th className="text-left p-2">Descrição</th>
            <th className="text-right p-2">Valor Padrão</th><th className="text-center p-2">Ativo</th><th className="w-24"></th>
          </tr></thead>
          <tbody>
            {list.map(t => (
              <tr key={t.id} className="border-t" data-testid={`tipo-${t.id}`}>
                <td className="p-2 font-medium">{t.nome}</td>
                <td className="p-2 text-muted-foreground">{t.descricao || '-'}</td>
                <td className="p-2 text-right">{fmt(t.valor_padrao)}</td>
                <td className="p-2 text-center">{t.ativo ? '✓' : '—'}</td>
                <td className="p-2 text-right">
                  <Button size="sm" variant="ghost" onClick={() => { setEdit(t); setShow(true); }}><Pencil className="w-3.5 h-3.5" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => onDelete(t.id)}><Trash2 className="w-3.5 h-3.5 text-rose-600" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {show && <TipoForm editing={edit} onClose={() => setShow(false)} onSaved={() => { setShow(false); load(); }} />}
    </CardContent></Card>
  );
}

function TipoForm({ editing, onClose, onSaved }) {
  const [form, setForm] = useState(editing || { nome: '', descricao: '', valor_padrao: 0, ativo: true });
  const save = async () => {
    try {
      const p = { ...form, valor_padrao: parseFloat(form.valor_padrao) || 0 };
      if (editing) await financeiroAPI.updateServiceType(editing.id, p);
      else await financeiroAPI.createServiceType(p);
      toast.success('Salvo');
      onSaved();
    } catch (e) { toast.error('Erro'); }
  };
  return (
    <Dialog open onOpenChange={onClose}><DialogContent data-testid="dialog-tipo">
      <DialogHeader><DialogTitle>{editing ? 'Editar' : 'Novo'} Tipo de Serviço</DialogTitle></DialogHeader>
      <div className="space-y-3">
        <div><Label>Nome *</Label><Input value={form.nome} onChange={(e) => setForm(f => ({ ...f, nome: e.target.value }))} data-testid="tipo-nome" /></div>
        <div><Label>Descrição</Label><Input value={form.descricao || ''} onChange={(e) => setForm(f => ({ ...f, descricao: e.target.value }))} /></div>
        <div><Label>Valor Padrão (R$)</Label><Input type="number" step="0.01" value={form.valor_padrao} onChange={(e) => setForm(f => ({ ...f, valor_padrao: e.target.value }))} /></div>
        <div className="flex items-center gap-2"><Switch checked={form.ativo} onCheckedChange={(v) => setForm(f => ({ ...f, ativo: v }))} /><span className="text-sm">Ativo</span></div>
      </div>
      <DialogFooter><Button variant="outline" onClick={onClose}>Cancelar</Button><Button onClick={save} data-testid="btn-save-tipo">Salvar</Button></DialogFooter>
    </DialogContent></Dialog>
  );
}

// ---------- Payment Methods ----------
function PaymentMethodsPanel() {
  const [list, setList] = useState([]);
  const [edit, setEdit] = useState(null);
  const [show, setShow] = useState(false);
  const load = async () => setList((await financeiroAPI.listPaymentMethods()).data || []);
  useEffect(() => { load(); }, []);
  const onDelete = async (id) => { if (!window.confirm('Excluir?')) return; await financeiroAPI.deletePaymentMethod(id); load(); };
  return (
    <Card><CardContent className="p-4">
      <div className="flex justify-between mb-4">
        <h3 className="font-semibold">Formas de Pagamento</h3>
        <Button onClick={() => { setEdit(null); setShow(true); }} data-testid="btn-add-metodo"><Plus className="w-4 h-4 mr-1" /> Nova</Button>
      </div>
      {list.length === 0 ? <p className="text-sm text-muted-foreground text-center py-6">Nenhuma cadastrada</p> : (
        <table className="w-full text-sm">
          <thead className="bg-muted/30"><tr><th className="text-left p-2">Nome</th><th className="text-left p-2">Descrição</th><th className="text-center p-2">Ativo</th><th className="w-24"></th></tr></thead>
          <tbody>
            {list.map(t => (
              <tr key={t.id} className="border-t" data-testid={`metodo-${t.id}`}>
                <td className="p-2 font-medium">{t.nome}</td>
                <td className="p-2 text-muted-foreground">{t.descricao || '-'}</td>
                <td className="p-2 text-center">{t.ativo ? '✓' : '—'}</td>
                <td className="p-2 text-right">
                  <Button size="sm" variant="ghost" onClick={() => { setEdit(t); setShow(true); }}><Pencil className="w-3.5 h-3.5" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => onDelete(t.id)}><Trash2 className="w-3.5 h-3.5 text-rose-600" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {show && <MetodoForm editing={edit} onClose={() => setShow(false)} onSaved={() => { setShow(false); load(); }} />}
    </CardContent></Card>
  );
}

function MetodoForm({ editing, onClose, onSaved }) {
  const [form, setForm] = useState(editing || { nome: '', descricao: '', ativo: true });
  const save = async () => {
    try {
      if (editing) await financeiroAPI.updatePaymentMethod(editing.id, form);
      else await financeiroAPI.createPaymentMethod(form);
      toast.success('Salvo'); onSaved();
    } catch (e) { toast.error('Erro'); }
  };
  return (
    <Dialog open onOpenChange={onClose}><DialogContent data-testid="dialog-metodo">
      <DialogHeader><DialogTitle>{editing ? 'Editar' : 'Nova'} Forma de Pagamento</DialogTitle></DialogHeader>
      <div className="space-y-3">
        <div><Label>Nome *</Label><Input value={form.nome} onChange={(e) => setForm(f => ({ ...f, nome: e.target.value }))} data-testid="metodo-nome" /></div>
        <div><Label>Descrição</Label><Input value={form.descricao || ''} onChange={(e) => setForm(f => ({ ...f, descricao: e.target.value }))} /></div>
        <div className="flex items-center gap-2"><Switch checked={form.ativo} onCheckedChange={(v) => setForm(f => ({ ...f, ativo: v }))} /><span className="text-sm">Ativo</span></div>
      </div>
      <DialogFooter><Button variant="outline" onClick={onClose}>Cancelar</Button><Button onClick={save} data-testid="btn-save-metodo">Salvar</Button></DialogFooter>
    </DialogContent></Dialog>
  );
}

// ---------- Tipos de Despesas (era "Categorias") ----------
function CategoriesPanel() {
  const [list, setList] = useState([]);
  const [edit, setEdit] = useState(null);
  const [show, setShow] = useState(false);
  const load = async () => {
    // Lista apenas despesas nesta tela (categorias antigas de "receita" ficam ocultas aqui)
    const r = await financeiroAPI.listCategories('despesa');
    setList(r.data || []);
  };
  useEffect(() => { load(); }, []);
  const onDelete = async (id) => { if (!window.confirm('Excluir?')) return; await financeiroAPI.deleteCategory(id); toast.success('Excluído'); load(); };
  return (
    <Card><CardContent className="p-4">
      <div className="flex justify-between mb-4">
        <h3 className="font-semibold">Tipos de Despesas</h3>
        <Button onClick={() => { setEdit(null); setShow(true); }} data-testid="btn-add-cat"><Plus className="w-4 h-4 mr-1" /> Novo</Button>
      </div>
      {list.length === 0 ? <p className="text-sm text-muted-foreground text-center py-6">Nenhum tipo de despesa cadastrado</p> : (
        <table className="w-full text-sm">
          <thead className="bg-muted/30"><tr>
            <th className="text-left p-2 w-14">Cor</th>
            <th className="text-left p-2">Nome</th>
            <th className="text-left p-2">Descrição</th>
            <th className="text-right p-2">Valor Padrão</th>
            <th className="text-center p-2">Ativo</th>
            <th className="w-24"></th>
          </tr></thead>
          <tbody>
            {list.map(t => (
              <tr key={t.id} className="border-t" data-testid={`cat-${t.id}`}>
                <td className="p-2"><span className="inline-block w-5 h-5 rounded" style={{ background: t.cor }}></span></td>
                <td className="p-2 font-medium">{t.nome}</td>
                <td className="p-2 text-muted-foreground">{t.descricao || '-'}</td>
                <td className="p-2 text-right">{fmt(t.valor_padrao)}</td>
                <td className="p-2 text-center">{t.ativo ? '✓' : '—'}</td>
                <td className="p-2 text-right">
                  <Button size="sm" variant="ghost" onClick={() => { setEdit(t); setShow(true); }}><Pencil className="w-3.5 h-3.5" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => onDelete(t.id)}><Trash2 className="w-3.5 h-3.5 text-rose-600" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {show && <CatForm editing={edit} onClose={() => setShow(false)} onSaved={() => { setShow(false); load(); }} />}
    </CardContent></Card>
  );
}

function CatForm({ editing, onClose, onSaved }) {
  const [form, setForm] = useState(editing || {
    nome: '',
    descricao: '',
    valor_padrao: 0,
    tipo: 'despesa',
    cor: '#e11d48',
    ativo: true,
  });
  const save = async () => {
    if (!form.nome?.trim()) { toast.error('Informe o nome'); return; }
    try {
      const payload = {
        ...form,
        tipo: 'despesa',
        valor_padrao: parseFloat(form.valor_padrao) || 0,
      };
      if (editing) await financeiroAPI.updateCategory(editing.id, payload);
      else await financeiroAPI.createCategory(payload);
      toast.success('Salvo'); onSaved();
    } catch (e) { toast.error('Erro'); }
  };
  return (
    <Dialog open onOpenChange={onClose}><DialogContent data-testid="dialog-cat">
      <DialogHeader><DialogTitle>{editing ? 'Editar' : 'Novo'} Tipo de Despesa</DialogTitle></DialogHeader>
      <div className="space-y-3">
        <div><Label>Nome *</Label><Input value={form.nome} onChange={(e) => setForm(f => ({ ...f, nome: e.target.value }))} data-testid="cat-nome" /></div>
        <div><Label>Descrição / Observação</Label><Input value={form.descricao || ''} onChange={(e) => setForm(f => ({ ...f, descricao: e.target.value }))} data-testid="cat-descricao" placeholder="Ex: combustível, materiais, etc." /></div>
        <div><Label>Valor Padrão (R$)</Label><Input type="number" step="0.01" value={form.valor_padrao} onChange={(e) => setForm(f => ({ ...f, valor_padrao: e.target.value }))} data-testid="cat-valor" /></div>
        <div><Label>Cor</Label><Input type="color" value={form.cor} onChange={(e) => setForm(f => ({ ...f, cor: e.target.value }))} /></div>
        <div className="flex items-center gap-2"><Switch checked={form.ativo} onCheckedChange={(v) => setForm(f => ({ ...f, ativo: v }))} /><span className="text-sm">Ativo</span></div>
      </div>
      <DialogFooter><Button variant="outline" onClick={onClose}>Cancelar</Button><Button onClick={save} data-testid="btn-save-cat">Salvar</Button></DialogFooter>
    </DialogContent></Dialog>
  );
}
